import apiClient from './client'
import { buildJsonApiQuery } from '@/utils/jsonapi';

/**
 * Metric Entries API module
 * Returns raw JSON:API responses - normalization happens in Redux thunks
 */
const metricEntriesAPI = {
  /**
   * Get all metric entries with optional filtering
   * @param {Object} [queryOptions] - Query options
   * @param {string} [queryOptions.metric_id] - Filter by metric ID
   * @param {Object} [queryOptions.filters] - Additional filters
   * @param {string} [queryOptions.sort] - Sort order
   * @param {string} [queryOptions.include] - Relations to include
   * @param {string} [queryOptions.partition_values] - Partition filter string (type:val,type:val)
   * @param {string} [queryOptions.collapse] - Collapse/group dimension type IDs (comma-separated)
   * @returns {Promise<Object>} JSON:API response with data, meta, links
   */
  getAll: async (queryOptions = {}) => {
    const filters = { ...queryOptions.filters };
    if (queryOptions.metric_id) {
      filters.metric = queryOptions.metric_id;
    }

    const params = buildJsonApiQuery({
      ...queryOptions,
      filters,
      // external_actor: EG-1's "who entered this" attribution for entries
      // submitted through the public embed form (mutually exclusive with
      // created_by) - sideloaded here so the entries table can render it
      // without a second request per entry.
      include: queryOptions.include || 'partition_values.partition_type,created_by,external_actor'
    });

    // Pass custom query params alongside JSON:API params
    if (queryOptions.partition_values) {
      params.partition_values = queryOptions.partition_values;
    }
    if (queryOptions.collapse) {
      params.collapse = queryOptions.collapse;
    }

    const response = await apiClient.get('/metric-entries/', { params });
    return response.data;
  },

  /**
   * Find existing (non-deleted) entries that match a metric + date, and
   * optionally the same campus and partition-value combination. Used to warn
   * about duplicates before creating a new entry (issue #104).
   *
   * @param {Object} params
   * @param {string} params.metricId - Metric UUID
   * @param {string} params.entryDate - Entry date (YYYY-MM-DD)
   * @param {Object} [params.partitionValues] - { [partitionTypeId]: partitionValueId }
   * @param {string} [params.campusId] - Campus UUID
   * @returns {Promise<Array>} Matching entry resource objects (empty if none)
   */
  findExisting: async ({ metricId, entryDate, partitionValues = {}, campusId } = {}) => {
    if (!metricId || !entryDate) return [];

    const filters = { entry_date: entryDate };
    if (campusId) filters.campus = campusId;

    const partition_values = Object.entries(partitionValues)
      .filter(([, valueId]) => valueId)
      .map(([typeId, valueId]) => `${typeId}:${valueId}`)
      .join(',');

    const doc = await metricEntriesAPI.getAll({
      metric_id: metricId,
      filters,
      partition_values: partition_values || undefined,
      pageSize: 5,
    });

    return doc?.data || [];
  },

  /**
   * Get a specific metric entry by ID
   * @param {string} entryId - Entry UUID
   * @returns {Promise<Object>} JSON:API response with metric entry data
   */
  getById: async (entryId) => {
    const response = await apiClient.get(`/metric-entries/${entryId}/`);
    return response.data;
  },

  /**
   * Create a new metric entry
   * @param {Object} data - Entry data
   * @returns {Promise<Object>} JSON:API response with created metric entry
   */
  create: async (data) => {
    const response = await apiClient.post('/metric-entries/', data);
    return response.data;
  },

  /**
   * Update a metric entry
   * @param {string} entryId - Entry UUID
   * @param {Object} data - Updated entry data
   * @returns {Promise<Object>} JSON:API response with updated metric entry
   */
  update: async (entryId, data) => {
    const response = await apiClient.patch(`/metric-entries/${entryId}/`, data);
    return response.data;
  },

  /**
   * Delete a metric entry
   * @param {string} entryId - Entry UUID
   * @returns {Promise<void>}
   */
  delete: async (entryId) => {
    await apiClient.delete(`/metric-entries/${entryId}/`);
  }
}

export default metricEntriesAPI
